/**
 * App-wide toast store. Decoupled from React (a plain pub/sub) so it can be
 * fired from services and the API layer, not just components. `<ToastHost/>`
 * (mounted once in App) subscribes and renders the stack.
 */

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

const DEFAULT_DURATION = 3500;

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<(items: ToastItem[]) => void>();

function emit(): void {
  for (const listener of listeners) listener(items);
}

export function subscribe(listener: (items: ToastItem[]) => void): () => void {
  listeners.add(listener);
  listener(items);
  return () => {
    listeners.delete(listener);
  };
}

export function dismissToast(id: number): void {
  items = items.filter((t) => t.id !== id);
  emit();
}

function push(message: string, type: ToastType): number {
  const id = ++seq;
  items = [...items, { id, message, type }];
  emit();
  setTimeout(() => dismissToast(id), DEFAULT_DURATION);
  return id;
}

export const toast = {
  success: (message: string) => push(message, 'success'),
  error: (message: string) => push(message, 'error'),
  info: (message: string) => push(message, 'info'),
  warning: (message: string) => push(message, 'warning'),
};
